/**
 * Case study copy for each Client View showcased on Midpoint Media.
 * Image filenames live in client/public/assets/midpoint-media/
 */
import { MIDPOINT_MEDIA_SECTION_IMAGES } from "./midpointMediaSectionImages.js";

export const MIDPOINT_MEDIA_CASE_STUDY_IDS = [
  "golf-homes",
  "casamapa",
  "automap",
  "systemize-fit",
  "community-intelligence",
  "hvac-ecosystem",
  "land-homestead",
  "local-business",
];

export const midpointMediaCaseStudyDefaults = {
  "golf-homes": {
    id: "golf-homes",
    status: "Live",
    title: "Golf Homes: The First Client View",
    subtitle:
      "How Midpoint turned scattered real estate and community signals into a map-driven Client View on a shared platform.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.golfHomes.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.golfHomes.alt,
    challenge:
      "Golf Homes needed a specialized experience for golf course communities — with map-based search, listing intelligence, CRM-connected leads, and market signals — without building a separate technology stack for each new vertical.",
    solution:
      "Midpoint launched Golf Homes as the first Client View on the Signal Intelligence Platform: a branded public experience connected to shared CRM, analytics, mapping, and workflow capabilities.",
    capabilities: [
      "Client View runtime with marketing and property search UX",
      "CRM lead capture with source attribution",
      "Map-based property discovery and saved search profiles",
      "Golf community and course context layers",
      "Sponsorship and partner workflow hooks",
      "Analytics, events, and conversion tracking",
    ],
    businessValue: [
      "Proof that Client Views can drive real engagement and lead flow",
      "Reusable patterns for real estate, partners, and inquiry workflows",
      "Foundation for additional signal-driven market experiences",
    ],
    nextSteps: [
      "Expand listing intelligence and market dashboards",
      "Deepen sponsorship and partner workflows",
      "Launch additional Client Views on the same platform core",
    ],
    cta: { label: "Request a demo", href: "/contact?form=demo" },
  },
  casamapa: {
    id: "casamapa",
    status: "Live",
    title: "CasaMapa: Map-Driven Real Estate Intelligence for Colombia",
    subtitle:
      "How Midpoint turned property listings, agency networks, and local market signals into a focused real estate Client View for Colombia.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.casamapaCaseStudy.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.casamapaCaseStudy.alt,
    challenge:
      "Colombia’s residential market spreads opportunity across listing portals, agency websites, WhatsApp threads, and local knowledge — making it hard for buyers and partners to see inventory, neighborhoods, and next actions in one place. Teams need a branded, map-first experience that still connects inquiries into CRM workflows.",
    solution:
      "Midpoint launched CasaMapa as a live Client View on the Signal Intelligence Platform: a Spanish-language, map-driven real estate experience combining listing discovery, location intelligence, agency profiles, buyer tools, and CRM-ready lead capture on one shared runtime.",
    capabilities: [
      "Map-based buy and rent discovery with location and property filters",
      "Listing detail pages with market and neighborhood context",
      "Agency directory and profile experiences for partner visibility",
      "Buyer account, saved searches, and compare workflows",
      "CRM lead capture and opportunity routing for inquiries",
      "Spanish-language UX and locale-aware Client View chrome",
    ],
    businessValue: [
      "Extends Midpoint’s proven real estate model into a distinct regional market",
      "Demonstrates how Client Views localize for language, currency, inventory, and partner networks",
      "Creates a measurable path from map discovery to agency follow-up and CRM action",
    ],
    nextSteps: [
      "Expand inventory coverage and agency onboarding across priority Colombian markets",
      "Deepen neighborhood guides, pricing intelligence, and seller intake workflows",
      "Grow partner, placement, and lead-routing programs for agencies and service providers",
    ],
    cta: { label: "View Live CasaMapa", href: "/v/casamapa" },
  },
  automap: {
    id: "automap",
    status: "Live",
    title: "AutoMapa: Vehicle Market Intelligence for Dealers and Buyers",
    subtitle:
      "How Midpoint productized automotive inventory, dealer workflows, and buyer signals into a focused Mobility Client View.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.automapCaseStudy.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.automapCaseStudy.alt,
    challenge:
      "Vehicle resellers and dealerships needed one place to surface inventory from multiple sources, capture qualified buyer interest, and understand market movement — without maintaining separate websites, spreadsheets, and ad-hoc lead tools.",
    solution:
      "AutoMapa became a live Client View on the Midpoint platform: a branded buyer experience for vehicle search and deal discovery, connected to dealer intake, CRM opportunities, and shared Mobility data pipelines.",
    capabilities: [
      "Map- and filter-driven vehicle inventory search",
      "Canonical listing display with market pricing signals",
      "Dealer and partner lead capture with CRM routing",
      "Dealer workspace for inventory and identity normalization",
      "Vehicle market analytics and class-level insights",
      "Shared platform auth, analytics, and API architecture",
    ],
    businessValue: [
      "Turns fragmented inventory into a searchable market product",
      "Connects buyer intent directly to dealer follow-up workflows",
      "Demonstrates vertical reuse of the Signal Intelligence Platform beyond real estate",
    ],
    nextSteps: [
      "Expand vehicle identity normalization and trim mapping",
      "Deepen dealer portal tools and partnership onboarding",
      "Roll out deal-finder and alert experiences for repeat buyers",
    ],
    cta: { label: "View Live AutoMapa", href: "/v/automap" },
  },
  "systemize-fit": {
    id: "systemize-fit",
    status: "Live",
    title: "Systemize Fit: One Workspace for Trainers, Gyms, and Clients",
    subtitle:
      "How Midpoint designed and launched a connected coaching operations product that replaces fragmented fitness tools with one branded platform.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.systemizeFitCaseStudy.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.systemizeFitCaseStudy.alt,
    challenge:
      "Independent trainers, gym operators, and their clients still run coaching work across disconnected apps — programming in one place, nutrition in another, scheduling in a calendar, and follow-up in chat. That fragmentation creates re-entry, missed context, and an uneven experience for the people paying for coaching.",
    solution:
      "Midpoint designed and launched Systemize Fit as a live coaching operations product: one branded workspace where trainers write programs, gyms manage staff and members, and clients stay on plan. Programming, nutrition, calendar, progress, messaging, and administration share the same client and session context.",
    capabilities: [
      "Role-based experiences for trainers, gym administrators, and clients",
      "Reusable workout programming with progressions and session delivery",
      "Nutrition targets and adherence tracking tied to training goals",
      "Shared calendar for sessions, availability, workload, and conflicts",
      "Progress analytics across clients, adherence, and coaching capacity",
      "In-product messaging and operational workflows for staff and members",
    ],
    businessValue: [
      "Gives fitness operators one system instead of a patchwork of point tools",
      "Creates a measurable path from session planning to client outcomes",
      "Shows Midpoint can productize operations software beyond map-first Client Views",
      "Launches a live, public SaaS brand at systemizefit.com",
    ],
    nextSteps: [
      "Deepen facility, staff, and membership operations for multi-location gyms",
      "Expand mobile session delivery and client accountability tools",
      "Add integrations for payments, wearables, and existing gym software",
    ],
    cta: { label: "View Live Systemize Fit", href: "https://systemizefit.com/" },
  },
  "community-intelligence": {
    id: "community-intelligence",
    status: "In Development",
    title: "Community Intelligence: A Map-First Picture of What Is Happening Locally",
    subtitle:
      "How Midpoint is building a civic Client View that combines real estate, safety, weather, culture, and infrastructure signals in one calm operating picture.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.communityIntelligenceCaseStudy.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.communityIntelligenceCaseStudy.alt,
    challenge:
      "Residents, relocators, and local organizations need to understand what is happening in an area — but the signals live in separate apps, dashboards, and news feeds with no shared geographic context.",
    solution:
      "Community Intelligence is Midpoint's map-first Client View for civic and neighborhood decision-making: layered signals, area summaries, and inquiry paths built on the same platform core that powers Golf Homes and AutoMapa.",
    capabilities: [
      "Map-first exploration with toggleable intelligence layers",
      "Real estate, safety, weather, and cultural event context",
      "Community reports and area summary panels",
      "CRM-ready inquiry and partnership intake",
      "Analytics on layer engagement and conversion paths",
      "Extensible data-source framework for regional rollouts",
    ],
    businessValue: [
      "Gives communities a trustworthy, practical intelligence surface",
      "Creates partnership paths for civic orgs, media, and local services",
      "Shows how Midpoint Map evolves from MVP layers into a full Client View product",
    ],
    nextSteps: [
      "Expand high-value regional layers and freshness monitoring",
      "Launch community report workflows and moderation tools",
      "Pilot with a first municipality or media partner audience",
    ],
    cta: { label: "Explore the Platform", href: "/community-intelligence" },
  },
  "hvac-ecosystem": {
    id: "hvac-ecosystem",
    status: "In Development",
    title: "HVAC Ecosystem Intelligence: Mapping a Fragmented Industry",
    subtitle:
      "How a licensing and workforce data pipeline can evolve into a map-centric market intelligence platform for manufacturers, distributors, contractors, and workforce planners.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.hvacEcosystemCaseStudy.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.hvacEcosystemCaseStudy.alt,
    challenge:
      "The HVAC industry is local, regional, and relationship-driven — yet the data that describes it is scattered across public licensing records, workforce filings, distributor footprints, training providers, and commercial directories. Distributors, manufacturers, recruiters, and private equity groups need to understand market structure and opportunity, but they are forced to stitch together spreadsheets, static lists, and one-off reports with no shared geographic context.",
    solution:
      "The HVAC Ecosystem Intelligence Platform reimagines a traditional licensing and workforce pipeline as a broader Client View: a searchable map that connects manufacturers, distributors, vendors, contractors, technicians, training providers, and employees into one ecosystem. Users explore the market geographically, score regions, analyze trends, and export canned reports for recruiting, sales, expansion, acquisition, and workforce planning.",
    capabilities: [
      "Unified entity search across contractors, technicians, distributors, and training providers",
      "Geographic visualization of workforce density and contractor coverage",
      "Distributor footprint analysis and underserved-region detection",
      "Market scoring, trend analysis, and competitive positioning views",
      "Canned reports for recruiting, sales, expansion, and acquisition planning",
      "Executive dashboards and operational views on a shared data model",
    ],
    businessValue: [
      "Turns scattered public, commercial, and operational data into decision-support intelligence",
      "Helps distributors and manufacturers spot expansion, coverage gaps, and sales opportunities",
      "Supports recruiters, staffing firms, and workforce agencies answering where shortages are emerging",
      "Creates multiple revenue paths: SaaS subscriptions, premium data, custom reports, and enterprise analytics",
    ],
    nextSteps: [
      "Expand entity normalization across licensing, workforce, and commercial sources",
      "Pilot market scoring and canned reports with a distributor or manufacturer partner",
      "Define premium data tiers and enterprise analytics packages for PE and software vendors",
    ],
    cta: { label: "Request a demo", href: "/contact?form=demo" },
  },
  "land-homestead": {
    id: "land-homestead",
    status: "Concept",
    title: "Land & Homestead Intelligence: Rural Opportunity on One Map",
    subtitle:
      "How a future Client View could help land buyers and builders evaluate parcels, risk, utilities, and buildability before they invest time on site.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.landHomesteadCaseStudy.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.landHomesteadCaseStudy.alt,
    challenge:
      "Rural land and homestead buyers juggle parcel records, zoning, water access, flood risk, slope, and utility reach across PDFs, county sites, and broker notes — slowing diligence and increasing costly surprises.",
    solution:
      "Land & Homestead Intelligence is a planned Client View that unifies parcel context, environmental risk, infrastructure visibility, and inquiry workflows into a single map-driven experience for rural investors and builders.",
    capabilities: [
      "Parcel boundaries with ownership and listing context",
      "Zoning and land-use overlays for build planning",
      "Water, utility, and access visibility layers",
      "Flood, slope, and hazard risk signals",
      "CRM intake for broker, builder, and surveyor follow-up",
      "Exportable area briefs for investor review",
    ],
    businessValue: [
      "Shortens early-stage land diligence for buyers and advisors",
      "Differentiates brokers and land specialists with mapped intelligence",
      "Opens a vertical product path for rural markets and homestead communities",
    ],
    nextSteps: [
      "Select a pilot region with strong parcel and hazard data coverage",
      "Validate layer priorities with land brokers and builders",
      "Design sponsor and referral workflows for regional service providers",
    ],
    cta: { label: "Request a Similar Client View", href: "/contact?form=demo" },
  },
  "local-business": {
    id: "local-business",
    status: "Concept",
    title: "Local Business Map: District Discovery with Sponsor Visibility",
    subtitle:
      "How a white-label Client View could help chambers and economic development teams showcase businesses, districts, and growth stories on a map.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.localBusinessCaseStudy.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.localBusinessCaseStudy.alt,
    challenge:
      "Chambers, downtown alliances, and economic development groups still rely on static directories that do not show geographic context, district boundaries, or a clear path from discovery to inquiry and sponsorship revenue.",
    solution:
      "Local Business Map is a concept Client View for regional growth teams: a branded directory tied to districts and corridors, with sponsor placements, category filters, and CRM-connected lead routing.",
    capabilities: [
      "Searchable business directory with map pins and district boundaries",
      "Category, hours, and service-area filters",
      "Sponsored placement zones for featured businesses",
      "Event and campaign landing support",
      "CRM intake for membership, sponsorship, and directory updates",
      "Analytics on discovery, clicks, and conversion by district",
    ],
    businessValue: [
      "Modernizes chamber and downtown marketing beyond PDF guides",
      "Creates recurring sponsorship inventory tied to real local discovery",
      "Gives economic development a visual story for visitors and investors",
    ],
    nextSteps: [
      "Pilot with one business district or downtown improvement district",
      "Define sponsor tiers and self-service listing update workflows",
      "Integrate event calendars and seasonal campaign modules",
    ],
    cta: { label: "Request a Similar Client View", href: "/contact?form=demo" },
  },
};

export function mergeMidpointMediaCaseStudies(contentCaseStudies = {}) {
  const merged = { ...midpointMediaCaseStudyDefaults };
  for (const [id, patch] of Object.entries(contentCaseStudies)) {
    if (!merged[id]) continue;
    merged[id] = {
      ...merged[id],
      ...patch,
      cta: { ...merged[id].cta, ...patch?.cta },
    };
  }
  return merged;
}

export function resolveMidpointMediaCaseStudy(content, page) {
  const caseStudies = content.caseStudies || {};
  const caseStudyId =
    page?.content?.caseStudyId ||
    (page?.content?.section === "golf-homes" ? "golf-homes" : null);
  if (!caseStudyId) return null;
  return caseStudies[caseStudyId] || null;
}
